import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDateTime, statusBadgeClass, statusLabel } from '../utils/formatters';
import { ShoppingCart, Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, X, Warehouse, Building2, FileText, Package, Check, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

function PurchaseOrderRow({ po, onView, onEdit, onDelete, onStatusAction }) {
  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{po.po_number || po.number || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(po.date)}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-200">{po.supplier?.name || '-'}</span>
        </div>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-200">{po.warehouse?.name || '-'}</span>
        </div>
      </td>
      <td>
        <span className={`badge ${statusBadgeClass(po.status)}`}>{statusLabel(po.status)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(po.subtotal)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(po.tax)}</span>
      </td>
      <td>
        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(po.total)}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onView(po)} className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded">
            <FileText className="h-4 w-4" />
          </button>
          {po.status === 'draft' && (
            <>
              <button onClick={() => onEdit(po)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded">
                <Edit2 className="h-4 w-4" />
              </button>
              <button onClick={() => onDelete(po)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
          {po.status === 'pending_approval' && (
            <button onClick={() => onStatusAction(po, 'approve')} className="p-1.5 text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20 rounded">
              <Check className="h-4 w-4" />
            </button>
          )}
          {po.status === 'approved' && (
            <button onClick={() => onStatusAction(po, 'cancel')} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
              <AlertTriangle className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function PurchaseOrdersPage() {
  const { hasPermission } = useAuth();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', supplier_id: '', status: '', date_from: '', date_to: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [form, setForm] = useState({
    supplier_id: '',
    warehouse_id: '',
    date: '',
    payment_terms: '',
    items: []
  });
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [itemQty, setItemQty] = useState('');
  const [itemUnitPrice, setItemUnitPrice] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPOS();
    fetchLookupData();
  }, [pagination.page, filters]);

  const fetchPOS = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/purchase-orders', { params });
      if (res.data.success) {
        setPos(res.data.data?.purchase_orders || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLookupData = async () => {
    try {
      const [suppliersRes, warehousesRes] = await Promise.all([
        api.get('/suppliers'),
        api.get('/warehouses')
      ]);
      setSuppliers(suppliersRes.data.data?.suppliers || suppliersRes.data.data || []);
      setWarehouses(warehousesRes.data.data?.warehouses || warehousesRes.data.data || []);
    } catch (error) {
      handleApiError(error);
    }
  };

  const fetchProducts = async (search) => {
    try {
      const res = await api.get('/products', { params: { search, limit: 10 } });
      setAvailableProducts(res.data.data?.products || res.data.data || []);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/purchase-orders', form);
      if (res.data.success) {
        toast.success('Purchase order berhasil dibuat');
        setShowCreateModal(false);
        setForm({ supplier_id: '', warehouse_id: '', date: '', payment_terms: '', items: [] });
        setItems([]);
        fetchPOS();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/purchase-orders/${selectedPO.id}`, form);
      if (res.data.success) {
        toast.success('Purchase order berhasil diperbarui');
        setShowEditModal(false);
        setSelectedPO(null);
        fetchPOS();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (po) => {
    if (!confirm(`Yakin hapus PO ${po.po_number || po.number}?`)) return;
    try {
      const res = await api.delete(`/purchase-orders/${po.id}`);
      if (res.data.success) {
        toast.success('Purchase order berhasil dihapus');
        fetchPOS();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleStatusAction = async (po, action) => {
    try {
      const res = await api.post(`/purchase-orders/${po.id}/${action}`);
      if (res.data.success) {
        toast.success(`Purchase order berhasil ${action}`);
        fetchPOS();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = (po) => {
    setSelectedPO(po);
    setForm({
      supplier_id: po.supplier_id || '',
      warehouse_id: po.warehouse_id || '',
      date: po.date?.split('T')[0] || '',
      payment_terms: po.payment_terms || '',
      items: []
    });
    setItems(po.items || []);
    setShowEditModal(true);
  };

  const addItem = () => {
    if (!selectedProduct || !itemQty || !itemUnitPrice) {
      toast.error('Pilih produk dan masukkan qty serta harga satuan');
      return;
    }
    const product = availableProducts.find(p => p.id === parseInt(selectedProduct));
    if (!product) return;
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      qty: parseFloat(itemQty),
      unit_price: parseFloat(itemUnitPrice),
      subtotal: parseFloat(itemQty) * parseFloat(itemUnitPrice)
    };
    setItems([...items, newItem]);
    setForm({...form, items: [...items, newItem]});
    setSelectedProduct('');
    setItemQty('');
    setItemUnitPrice('');
    setAvailableProducts([]);
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    setForm({...form, items: newItems});
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const tax = subtotal * 0.1;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Manajemen Purchase Order</h1>
          <p className="page-subtitle">Kelola pesanan pembelian dari supplier</p>
        </div>
        {hasPermission('create_purchase_orders') && (
          <button onClick={() => { setForm({ supplier_id: '', warehouse_id: '', date: '', payment_terms: '', items: [] }); setItems([]); setShowCreateModal(true); }} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Buat PO Baru
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nomor PO, supplier..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchPOS()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Menunggu Persetujuan</option>
                <option value="approved">Disetujui</option>
                <option value="sent">Terkirim</option>
                <option value="received">Diterima</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                className="input"
                placeholder="Dari tanggal"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </div>
            <div>
              <input
                type="date"
                className="input"
                placeholder="Sampai tanggal"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={fetchPOS} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '', date_from: '', date_to: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-16" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-24" />
                </div>
              ))}
            </div>
          </div>
        ) : pos.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada purchase order yang dibuat.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nomor PO</th>
                    <th>Tanggal</th>
                    <th>Supplier</th>
                    <th>Gudang</th>
                    <th>Status</th>
                    <th>Subtotal</th>
                    <th>Tax</th>
                    <th>Total</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pos.map((po) => (
                    <PurchaseOrderRow
                      key={po.id}
                      po={po}
                      onView={() => { setSelectedPO(po); setShowDetailsModal(true); }}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onStatusAction={handleStatusAction}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || (showEditModal && selectedPO)) && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">{showCreateModal ? 'Buat Purchase Order Baru' : 'Edit Purchase Order'}</h3>
            </div>
            <form onSubmit={showCreateModal ? handleCreate : handleUpdate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Supplier <span className="text-danger-600">*</span></label>
                  <select className="input" value={form.supplier_id} onChange={(e) => setForm({...form, supplier_id: e.target.value})} required>
                    <option value="">Pilih Supplier</option>
                    {suppliers.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="label">Gudang <span className="text-danger-600">*</span></label>
                  <select className="input" value={form.warehouse_id} onChange={(e) => setForm({...form, warehouse_id: e.target.value})} required>
                    <option value="">Pilih Gudang</option>
                    {warehouses.map(w => (<option key={w.id} value={w.id}>{w.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="label">Tanggal <span className="text-danger-600">*</span></label>
                  <input type="date" className="input" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} required />
                </div>
                <div>
                  <label className="label">Syarat Pembayaran</label>
                  <input type="text" className="input" value={form.payment_terms} onChange={(e) => setForm({...form, payment_terms: e.target.value})} placeholder="Contoh: 30 hari, Cash, etc" />
                </div>
              </div>

              {/* Items Section */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold">Item PO</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <label className="label">Produk</label>
                    <div className="relative">
                      <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        className="input pl-9"
                        placeholder="Cari produk..."
                        value={selectedProduct ? availableProducts.find(p => p.id === parseInt(selectedProduct))?.name || '' : ''}
                        onChange={(e) => { setSelectedProduct(''); fetchProducts(e.target.value); }}
                      />
                      {availableProducts.length > 0 && (
                        <div className="absolute top-full mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                          {availableProducts.map(p => (
                            <div key={p.id} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer" onClick={() => { setSelectedProduct(p.id); setAvailableProducts([]); }}>
                              <span className="text-sm font-medium">{p.name}</span>
                              <span className="text-xs text-gray-500 ml-2">SKU: {p.sku}</span>
                              <span className="text-xs text-gray-400 ml-2">Stok: {p.stock}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="label">Qty</label>
                    <input type="number" className="input" value={itemQty} onChange={(e) => setItemQty(e.target.value)} placeholder="1" step="0.01" />
                  </div>
                  <div>
                    <label className="label">Harga Satuan</label>
                    <input type="number" className="input" value={itemUnitPrice} onChange={(e) => setItemUnitPrice(e.target.value)} placeholder="0" step="0.01" />
                  </div>
                  <button type="button" onClick={addItem} className="btn-primary btn-sm">Tambah</button>
                </div>

                {/* Items Table */}
                {items.length > 0 && (
                  <div className="table-container">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Produk</th>
                          <th>SKU</th>
                          <th>Qty</th>
                          <th>Harga Satuan</th>
                          <th>Subtotal</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.product_name}</td>
                            <td>{item.sku}</td>
                            <td>{item.qty}</td>
                            <td>{formatCurrency(item.unit_price)}</td>
                            <td>{formatCurrency(item.subtotal)}</td>
                            <td>
                              <button onClick={() => removeItem(index)} className="p-1 text-danger-600 hover:bg-danger-50 rounded">
                                <X className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totals */}
                {items.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                        <span className="font-semibold ml-2">{formatCurrency(calculateTotals().subtotal)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Tax (10%):</span>
                        <span className="font-semibold ml-2">{formatCurrency(calculateTotals().tax)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Total:</span>
                        <span className="font-semibold ml-2 text-lg">{formatCurrency(calculateTotals().total)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowCreateModal(false); setShowEditModal(false); setSelectedPO(null); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedPO && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Purchase Order</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-sm font-semibold">Nomor PO:</label>
                  <p className="text-gray-900 dark:text-white">{selectedPO.po_number || selectedPO.number}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Tanggal:</label>
                  <p className="text-gray-900 dark:text-white">{formatDateTime(selectedPO.date)}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Supplier:</label>
                  <p className="text-gray-900 dark:text-white">{selectedPO.supplier?.name}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Gudang:</label>
                  <p className="text-gray-900 dark:text-white">{selectedPO.warehouse?.name}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Status:</label>
                  <span className={`badge ${statusBadgeClass(selectedPO.status)}`}>{statusLabel(selectedPO.status)}</span>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Total:</label>
                  <p className="text-gray-900 dark:text-white">{formatCurrency(selectedPO.total)}</p>
                </div>
              </div>

              {selectedPO.items && selectedPO.items.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold mb-3">Item Details</h4>
                  <div className="table-container">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Produk</th>
                          <th>SKU</th>
                          <th>Qty</th>
                          <th>Harga Satuan</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPO.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.product_name || item.product?.name}</td>
                            <td>{item.sku || item.product?.sku}</td>
                            <td>{item.qty}</td>
                            <td>{formatCurrency(item.unit_price || item.price)}</td>
                            <td>{formatCurrency(item.subtotal || (item.qty * (item.unit_price || item.price)))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button onClick={() => { setShowDetailsModal(false); setSelectedPO(null); }} className="btn-primary flex-1">Tutup</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}